import { useCallback, useEffect, useState } from 'react';
import {
  warehouseCapacityPlanningApi,
  type DigitalTwin,
  type DockPlan,
  type LaborPlan,
  type StoragePlan,
  type WarehouseCapacityPlan,
  type WarehouseConstraint,
  type WarehouseDashboard,
  type WarehouseRecommendation,
  type WarehouseScenario,
} from '../../api/warehousecapacityplanning';

type Tab = 'dashboard' | 'storage' | 'docks' | 'labor' | 'twin' | 'constraints' | 'recommendations' | 'scenarios';
type Theme = 'light' | 'dark';

function fmtMoney(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export function WarehouseCapacityPlanningPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<WarehouseDashboard | null>(null);
  const [plan, setPlan] = useState<WarehouseCapacityPlan | null>(null);
  const [storage, setStorage] = useState<StoragePlan[]>([]);
  const [docks, setDocks] = useState<DockPlan[]>([]);
  const [labor, setLabor] = useState<LaborPlan[]>([]);
  const [twin, setTwin] = useState<DigitalTwin | null>(null);
  const [constraints, setConstraints] = useState<WarehouseConstraint[]>([]);
  const [recommendations, setRecommendations] = useState<WarehouseRecommendation[]>([]);
  const [scenarios, setScenarios] = useState<WarehouseScenario[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, plans] = await Promise.all([warehouseCapacityPlanningApi.dashboard(), warehouseCapacityPlanningApi.listPlans()]);
      setDashboard(dash);
      const p = plans[0] ?? null;
      setPlan(p);
      if (!p) return;
      const [stor, dock, lab, cons, recs, sc, dt] = await Promise.all([
        warehouseCapacityPlanningApi.listStoragePlans(p.id),
        warehouseCapacityPlanningApi.listDockPlans(p.id),
        warehouseCapacityPlanningApi.listLaborPlans(p.id),
        warehouseCapacityPlanningApi.listConstraints(p.id),
        warehouseCapacityPlanningApi.listRecommendations(p.id),
        warehouseCapacityPlanningApi.listScenarios(p.id),
        warehouseCapacityPlanningApi.getDigitalTwin(p.id),
      ]);
      setStorage(stor);
      setDocks(dock);
      setLabor(lab);
      setConstraints(cons);
      setRecommendations(recs);
      setScenarios(sc);
      setTwin(dt);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load warehouse capacity planning');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const shell = dark ? 'min-h-screen bg-slate-950 text-slate-100' : 'min-h-screen bg-slate-50 text-slate-900';
  const card = dark ? 'rounded-xl border border-slate-800 bg-slate-900 p-5' : 'rounded-xl border border-slate-200 bg-white p-5 shadow-sm';
  const header = dark ? 'border-b border-slate-800 bg-slate-900' : 'border-b border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-600';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'storage', label: 'Storage Heatmap' },
    { id: 'docks', label: 'Dock Scheduling' },
    { id: 'labor', label: 'Labor Planning' },
    { id: 'twin', label: 'Digital Twin' },
    { id: 'constraints', label: 'Constraints' },
    { id: 'recommendations', label: 'Recommendations' },
    { id: 'scenarios', label: 'Scenarios' },
  ];

  return (
    <div className={shell}>
      <div className={`${header} px-6 py-4`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Enterprise Warehouse Capacity Planning</h1>
            <p className={`mt-1 text-sm ${muted}`}>Warehouse digital twin, capacity optimization, dock scheduling, labor planning, and fulfillment readiness</p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg px-3 py-2 text-sm ${dark ? 'bg-slate-800' : 'bg-slate-100'}`} aria-label="Toggle dark mode">{dark ? 'Light' : 'Dark'}</button>
            <button type="button" onClick={refresh} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">Refresh</button>
          </div>
        </div>
        {plan && (
          <p className={`mt-2 text-sm ${muted}`}>
            {plan.name} · Fulfillment Readiness {(plan.fulfillment_readiness_score * 100).toFixed(0)}% · Health {plan.health_score.toFixed(0)} · Buffer {plan.capacity_buffer.toFixed(1)}%
          </p>
        )}
      </div>
      {error && <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300" role="alert">{error}</div>}
      <div className={`${header} px-6`}>
        <nav className="flex gap-1 overflow-x-auto" role="tablist">
          {tabs.map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
              className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium ${tab === t.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500'}`}>{t.label}</button>
          ))}
        </nav>
      </div>
      <div className="p-6">
        {tab === 'dashboard' && dashboard && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Active Plans', value: dashboard.active_plans },
              { label: 'Storage Utilization', value: `${dashboard.avg_storage_utilization.toFixed(0)}%` },
              { label: 'Dock Utilization', value: `${dashboard.avg_dock_utilization.toFixed(0)}%` },
              { label: 'Labor Productivity', value: `${dashboard.avg_labor_productivity.toFixed(0)}%` },
              { label: 'Fulfillment Readiness', value: `${(dashboard.fulfillment_readiness_score * 100).toFixed(0)}%` },
              { label: 'Capacity Buffer', value: `${dashboard.capacity_buffer.toFixed(1)}%` },
              { label: 'Throughput', value: dashboard.throughput.toLocaleString() },
              { label: 'Health Score', value: dashboard.health_score.toFixed(0) },
              { label: 'Resilience Index', value: dashboard.resilience_index.toFixed(0) },
              { label: 'Active Constraints', value: dashboard.constraint_count },
              { label: 'Storage Opt Score', value: dashboard.storage_optimization_score.toFixed(0) },
              { label: 'Pending Recommendations', value: dashboard.pending_recommendations },
            ].map((m) => (
              <div key={m.label} className={card}><p className={`text-sm ${muted}`}>{m.label}</p><p className="mt-1 text-2xl font-semibold">{m.value}</p></div>
            ))}
          </div>
        )}
        {tab === 'storage' && storage.map((s) => (
          <div key={s.id} className={`${card} mb-4`}>
            <h3 className="font-medium">{s.zone_name}</h3>
            <p className={`mt-1 text-sm ${muted}`}>{s.storage_type.replace(/_/g, ' ')} · ABC {s.abc_class} · {s.velocity_tier} velocity</p>
            <div className="mt-2 h-2 rounded-full bg-slate-200 dark:bg-slate-700">
              <div className={`h-2 rounded-full ${s.utilization_pct >= 90 ? 'bg-red-500' : 'bg-indigo-600'}`} style={{ width: `${Math.min(s.utilization_pct, 100)}%` }} />
            </div>
            <p className={`mt-1 text-sm ${muted}`}>{s.utilization_pct.toFixed(0)}% utilized · {s.optimization_note}</p>
          </div>
        ))}
        {tab === 'docks' && docks.map((d) => (
          <div key={d.id} className={`${card} mb-4`}>
            <div className="flex items-center justify-between">
              <h3 className="font-medium">{d.dock_name}</h3>
              {d.conflict_count > 0 && <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-900 dark:text-amber-200">{d.conflict_count} conflicts</span>}
            </div>
            <p className={`mt-1 text-sm ${muted}`}>{d.direction} · {d.utilization_pct.toFixed(0)}% · {d.carrier_name ?? 'Multi-carrier'}</p>
          </div>
        ))}
        {tab === 'labor' && labor.map((l) => (
          <div key={l.id} className={`${card} mb-4`}>
            <h3 className="font-medium">{l.role} — {l.shift_name}</h3>
            <p className={`mt-1 text-sm ${muted}`}>Headcount {l.headcount}/{l.required_headcount} · Utilization {l.utilization_pct.toFixed(0)}% · Cost {fmtMoney(l.labor_cost)}</p>
            {l.overtime_hours > 0 && <p className="mt-1 text-sm text-amber-600">Overtime: {l.overtime_hours}h</p>}
          </div>
        ))}
        {tab === 'twin' && twin && (
          <div className={card}>
            <h3 className="text-lg font-semibold">{twin.warehouse_name} Digital Twin</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div><p className={`text-sm ${muted}`}>Layout Zones</p><p className="text-xl font-semibold">{twin.layout_zones}</p></div>
              <div><p className={`text-sm ${muted}`}>Total Storage</p><p className="text-xl font-semibold">{twin.total_storage.toLocaleString()}</p></div>
              <div><p className={`text-sm ${muted}`}>Docks</p><p className="text-xl font-semibold">{twin.total_docks}</p></div>
              <div><p className={`text-sm ${muted}`}>Labor</p><p className="text-xl font-semibold">{twin.labor_headcount}</p></div>
            </div>
            {twin.bottleneck_nodes.length > 0 && (
              <div className="mt-4">
                <p className={`text-sm font-medium ${muted}`}>Bottleneck Nodes</p>
                <ul className="mt-2 list-inside list-disc text-sm">
                  {twin.bottleneck_nodes.map((b) => <li key={b}>{b}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
        {tab === 'constraints' && constraints.map((c) => (
          <div key={c.id} className={`${card} mb-4`}>
            <h3 className="font-medium">{c.type.replace(/_/g, ' ')} · {c.severity}</h3>
            <p className={`mt-1 text-sm ${muted}`}>Probability {(c.probability * 100).toFixed(0)}% · {c.resource_name}</p>
            <p className="mt-2 text-sm">{c.description}</p>
            <p className="mt-1 text-sm">Mitigation: {c.mitigation}</p>
          </div>
        ))}
        {tab === 'recommendations' && recommendations.map((r) => (
          <div key={r.id} className={`${card} mb-4`}>
            <h3 className="font-medium">{r.title}</h3>
            <p className={`mt-1 text-sm ${muted}`}>{r.type.replace(/_/g, ' ')} · Confidence {(r.confidence * 100).toFixed(0)}% · Savings {fmtMoney(r.expected_savings)}</p>
            <p className="mt-2 text-sm">{r.business_explanation}</p>
          </div>
        ))}
        {tab === 'scenarios' && scenarios.map((s) => (
          <div key={s.id} className={`${card} mb-4`}>
            <h3 className="font-medium">{s.name}</h3>
            <p className={`mt-1 text-sm ${muted}`}>{s.scenario_type.replace(/_/g, ' ')}</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-4">
              <span>Throughput {s.throughput.toLocaleString()}</span>
              <span>Storage {s.storage_utilization.toFixed(0)}%</span>
              <span>Labor {s.labor_utilization.toFixed(0)}%</span>
              <span>Readiness {(s.fulfillment_readiness * 100).toFixed(0)}%</span>
            </div>
            {s.description && <p className="mt-2 text-sm">{s.description}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
